import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    HostListener,
    Inject,
    InjectionToken,
    Input,
    Optional,
    ViewEncapsulation,
} from '@angular/core';

import isEqual from 'lodash-es/isEqual';
import { Subject } from 'rxjs';

const MAX_LENGTH_SUBTITLE = 70;

/**
 * Describes a parent component that manages a list of options.
 * Contains properties that the options can inherit.
 * @docs-private
 */
interface UiSelectCardGroupComponent {
    disabled?: boolean;
    readonly?: boolean;
    multiple?: boolean;
}

/**
 * Injection token used to provide the parent component to options.
 */
export const UI_SELECT_CARD_PARENT_COMPONENT =
    new InjectionToken<UiSelectCardGroupComponent>('UI_SELECT_CARD_PARENT_COMPONENT');

interface IToggleSelectCardEvent {
    select: boolean;
    value: any;
}

@Component({
    selector: 'ui-select-card',
    templateUrl: './ui-select-card.component.html',
    styleUrls: ['./ui-select-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiSelectCardComponent {

    @HostBinding('tabindex')
    public get tabindex() {
        return this.disabled ?
            -1 : 0;
    }

    @HostBinding('attr.aria-label')
    public get ariaLabel() {
        return `${this.title}. ${this.subtitle}`;
    }

    public get safeSubtitle() {
        return this.subtitle ?
            this.subtitle.length > this.maxLengthSubtitle ?
                this.subtitle.slice(0, this.maxLengthSubtitle) + '...' :
                this.subtitle : '';
    }

    @Input()
    public set value(value: any) {
        if (isEqual(this._value, value)) { return; }

        this._value = value;
    }
    public get value() {
        return this._value;
    }

    public set selected(select: boolean) {
        if (select === this._selected) { return; }
        this._selected = select;
        this._cd.detectChanges();
    }
    public get selected() {
        return this._selected;
    }

    public toggleSelect$ = new Subject<IToggleSelectCardEvent>();

    @Input()
    public title = '';

    @Input()
    public subtitle = '';

    @Input()
    public matIcon = '';

    @Input()
    public svgIcon = '';

    @Input()
    public maxLengthSubtitle = MAX_LENGTH_SUBTITLE;

    @HostBinding('class.disabled')
    @Input()
    public get disabled() {
        return !!(this._parent && this._parent.disabled) || this._disabled;
    }
    public set disabled(disable: boolean) {
        if (disable === this._disabled) { return; }
        this._disabled = disable;
    }

    @HostBinding('class.readonly')
    @Input()
    public get readonly() {
        return !!(this._parent && this._parent.readonly) || this._readonly;
    }
    public set readonly(readonly: boolean) {
        if (readonly === this._readonly) { return; }
        this._readonly = readonly;
    }

    private _readonly = false;
    private _disabled = false;

    private _selected = false;

    private _value?: any;

    constructor(
        private _cd: ChangeDetectorRef,
        @Optional()
        @Inject(UI_SELECT_CARD_PARENT_COMPONENT)
        private _parent: UiSelectCardGroupComponent,
    ) {
    }

    @HostListener('keydown.space', ['$event'])
    @HostListener('keyup.space', ['$event'])
    @HostListener('keyup.enter', ['$event'])
    @HostListener('click', ['$event'])
    public toggleSelect(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        if (event.type === 'keydown') { return; }

        this.toggleSelect$.next({
            value: this.value,
            select: !this.selected,
        });
    }

}
